<template>
  <div class="w-full">
    <!-- Greenhouse projects (cards and details, every state): left-aligned initials + investor count, total loan, available and interest start, no bar -->
    <div v-if="greenhouse" class="flex flex-col items-start gap-1 text-left text-white mb-2" :class="{ 'px-6': !contain }">
      <!-- One dark pill per item, so the text stays readable over any photo -->
      <InvestorAvatars class="rounded-full bg-black bg-opacity-50 p-0.5 pr-3" :initials="investorInitials" :count="investors" />
      <span class="inline-flex items-baseline rounded-full bg-black bg-opacity-50 px-3 py-0.5 text-sm whitespace-nowrap">
        <span class="font-bold">${{ dollarFormatting(goal, false) }}</span>
        <span class="text-11 text-white ml-1">{{ $t('projects.greenhouse.loan_short') }}</span>
      </span>
      <!-- Reserved and funded: nothing left for the community -->
      <span class="inline-flex rounded-full bg-black bg-opacity-50 px-3 py-0.5 text-11 text-white">
        {{ $t('projects.greenhouse.available_short', { amount: '$' + dollarFormatting(state === 'greenhouse' ? availableAmount : 0, false) }) }}
      </span>
      <!-- Funded: nobody can start earning interest any more -->
      <span v-if="daysToInterest !== null && state !== 'funded'" class="inline-flex rounded-full bg-black bg-opacity-50 px-3 py-0.5 text-11 text-white">
        {{ $t('projects.greenhouse.interest_days', { dayCount: daysToInterest }, daysToInterest) }}
      </span>
    </div>
    <div
      v-else
      class="flex justify-between mb-2"
      :class="{ 'px-6': !contain }"
    >
      <div>
        <p class="text-11 text-white text-opacity-60 text-left" v-if="details">
          {{ $t('wording.raised') }}
        </p>
        <div class="font-bold text-green-1 text-sm" v-if="current !== null && (state === 'campaign' || state === 'reserved')">
          ${{ dollarFormatting(current, false) }} 
        </div>
        <div class="text-grey-7 text-sm font-semibold" v-else>
          {{ state === 'funded' ? $t(
          'projects.state_options.funded',
          { investorCount: investors },
          investors
        ) : $t('projects.details.goal') }}
        </div>
      </div>
      <div v-if="reserved && details">
        <p class="text-11 text-white text-opacity-60 text-left">
          {{ $t('projects.state_options.reserved') }}
        </p>
        <div class="font-bold text-green-1 text-sm">
          ${{ dollarFormatting(reserved, false) }} 
        </div>
      </div>
      <div>
        <p class="text-11 text-white text-opacity-60 text-left" v-if="details">
          {{ $t('wording.goal') }}
        </p>
        <div
          class="text-white text-sm"
          :class="{ 'font-bold': current === null }"
        >
          ${{ dollarFormatting(goal, false) }} 
        </div>
      </div>
    </div>
    <div class="h-2">
      <template v-if="current !== null && state !== 'funded' && !greenhouse">
        <div class="progress-bar opacity-30 bg-white w-full" v-if="state === 'campaign' || state === 'reserved'"></div>
        <div
          class="progress-bar w-24 bg-green-1"
          :style="'width: ' + ((current + reserved) / goal) * 100 + '%;'"
        ></div>
        <div
          class="progress-bar w-24 bg-green-2"
          :style="'width: ' + (current / goal) * 100 + '%;'"
        ></div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import useGlobalHelpers from '@/mixins/useGlobalHelpers.js'
import InvestorAvatars from '@/components/general/InvestorAvatars.vue'

export default {
  components: { InvestorAvatars },
  props: {
    goal: {
      type: Number,
      default: 0
    },
    placeholder: {
      type: String,
      default: 'Investment goal'
    },
    current: {
      type: Number,
      default: null
    },
    reserved: {
      type: Number,
      default: 0
    },
    investors: {
      type: Number,
      default: 0
    },
    investorInitials: {
      type: Array,
      default: () => []
    },
    labels: {
      type: Boolean,
      default: false
    },
    details: {
      type: Boolean,
      default: false
    },
    contain: {
      type: Boolean,
      default: false
    },
    state: {
      type: String,
      default: 'campaign'
    },
    // API greenhouse block (retail, greenhouse, retail_cap, available, next_close_date), null for regular projects
    greenhouse: {
      type: Object,
      default: null
    },
  },
  setup(props) {
    const { dollarFormatting } = useGlobalHelpers()

    // Days until the next greenhouse close, when community investments start earning interest
    const daysToInterest = computed(() => {
      const closeDate = props.greenhouse?.next_close_date
      if (!closeDate) return null
      const diffTime = new Date(closeDate.replace(/-/g, '/')) - new Date()
      return Math.max(Math.ceil(diffTime / (1000 * 60 * 60 * 24)), 0)
    })

    // In cents, rounded down to whole dollars
    const availableAmount = computed(() => {
      return props.greenhouse ? Math.floor((props.greenhouse.available || 0) / 100) * 100 : 0
    })

    return {
      dollarFormatting,
      daysToInterest,
      availableAmount
    }
  }
}
</script>
<style lang="postcss" scoped>
.progress-bar {
  @apply h-2 absolute bottom-0;
}
</style>
